import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, Filter, GripVertical } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';

function SortableTh({ col }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: col.key });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
  };

  return (
    <th
      ref={setNodeRef}
      style={style}
      className={`group relative px-4 py-2.5 font-semibold whitespace-nowrap ${
        col.align === 'right' ? 'text-right' : 'text-left'
      } ${isDragging ? 'z-10 bg-slate-100' : ''}`}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        aria-label={`Reorder column ${typeof col.label === 'string' ? col.label : col.key}`}
        className="absolute left-0.5 top-1/2 -translate-y-1/2 shrink-0 cursor-grab text-slate-400 opacity-0 hover:text-slate-600 focus:opacity-100 active:cursor-grabbing group-hover:opacity-100"
      >
        <GripVertical size={12} strokeWidth={1.75} />
      </button>
      {col.label}
    </th>
  );
}

function PlainTh({ col }) {
  return (
    <th
      className={`px-4 py-2.5 font-semibold whitespace-nowrap ${
        col.align === 'right' ? 'text-right' : 'text-left'
      }`}
    >
      {col.label}
    </th>
  );
}

function ColumnFilterControl({ col, filter, onChange, labels }) {
  const active = filter !== undefined;
  const value = filter?.value ?? '';
  const hint = [
    filter?.value === null ? labels.empty : null,
    filter?.currency,
    active ? `${labels.clear} (Esc)` : null,
  ].filter(Boolean).join(' · ') || undefined;
  const clearOnEscape = (event) => {
    if (event.key === 'Escape' && active) onChange(col.key, undefined);
  };
  const inputClass = `h-7 w-full min-w-[110px] rounded border px-2 text-[11px] font-normal normal-case tracking-normal outline-none ${
    active ? 'border-primary bg-primary/5 text-primary' : 'border-slate-200 bg-white text-slate-700'
  }`;
  return (
    <div onClick={(event) => event.stopPropagation()}>
      {col.filterType === 'select' ? (
        <select
          aria-label={`${labels.filter} ${col.label}`}
          aria-keyshortcuts={active ? 'Escape' : undefined}
          title={hint}
          className={inputClass}
          value={value}
          onKeyDown={clearOnEscape}
          onChange={(event) => onChange(col.key, event.target.value ? { value: event.target.value, exact: true } : undefined)}
        >
          <option value="">{filter?.value === null ? labels.empty : labels.filter}</option>
          {col.filterOptions.map(({ value: optionValue, label }) => (
            <option key={optionValue} value={optionValue}>{label}</option>
          ))}
        </select>
      ) : (
        <input
          aria-label={`${labels.filter} ${col.label}`}
          aria-keyshortcuts={active ? 'Escape' : undefined}
          title={hint}
          type={col.filterType === 'date' ? 'date' : col.filterType === 'number' ? 'number' : 'text'}
          step={col.filterType === 'number' ? 'any' : undefined}
          placeholder={filter?.value === null ? labels.empty : labels.filter}
          className={inputClass}
          value={value}
          onKeyDown={clearOnEscape}
          onChange={(event) => onChange(col.key, event.target.value ? { value: event.target.value } : undefined)}
        />
      )}
    </div>
  );
}

function FilterableCell({ col, row, onFilterOnValue, labels }) {
  const content = col.render ? col.render(row[col.key], row) : row[col.key];
  if (!onFilterOnValue || !col.filterType) return content;
  const values = col.filterValues ? col.filterValues(row) : [row[col.key]];
  const options = (values || []).filter((value) => value !== undefined && value !== '');
  if (!options.length) options.push(null);
  return (
    <div className="group/cell flex items-center gap-1.5">
      <div className="min-w-0">{content}</div>
      {options.length === 1 ? (
        <button
          type="button"
          title={labels.onValue}
          aria-label={`${labels.onValue}: ${col.label}`}
          onClick={(event) => { event.stopPropagation(); onFilterOnValue(col.key, options[0], row); }}
          className="shrink-0 p-1 rounded text-slate-400 hover:bg-primary/10 hover:text-primary opacity-0 group-hover/cell:opacity-100 focus:opacity-100"
        >
          <Filter size={12} />
        </button>
      ) : (
        <select
          title={labels.onValue}
          aria-label={`${labels.onValue}: ${col.label}`}
          value=""
          onClick={(event) => event.stopPropagation()}
          onChange={(event) => { event.stopPropagation(); onFilterOnValue(col.key, event.target.value, row); }}
          className="w-6 shrink-0 text-slate-500 opacity-0 group-hover/cell:opacity-100 focus:opacity-100"
        >
          <option value="">⋯</option>
          {options.map((value, index) => <option key={`${value}-${index}`} value={value}>{value}</option>)}
        </select>
      )}
    </div>
  );
}

export function Table({
  columns = [],
  rows = [],
  rowKey = '_id',
  onRowClick,
  loading = false,
  emptyState,
  page,
  total,
  perPage = 10,
  onPrev,
  onNext,
  columnOrder,
  onColumnOrderChange,
  groupBy,
  renderGroupHeader,
  columnFilters,
  onColumnFilterChange,
  onFilterOnValue,
  filterLabels,
}) {
  const hasPagination = page != null && total != null;
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());

  const orderedColumns = useMemo(() => {
    if (!columnOrder || columnOrder.length === 0) return columns;
    const byKey = new Map(columns.map((c) => [c.key, c]));
    const ordered = columnOrder.map((k) => byKey.get(k)).filter(Boolean);
    const seen = new Set(ordered.map((c) => c.key));
    const appended = columns.filter((c) => !seen.has(c.key));
    return [...ordered, ...appended];
  }, [columns, columnOrder]);

  const groupedSections = useMemo(() => {
    if (!groupBy) return null;
    const order = [];
    const map = new Map();
    rows.forEach((row) => {
      const key = groupBy(row);
      if (!map.has(key)) {
        map.set(key, []);
        order.push(key);
      }
      map.get(key).push(row);
    });
    return order.map((key) => [key, map.get(key)]);
  }, [rows, groupBy]);

  function toggleGroup(key) {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleDragEnd({ active, over }) {
    if (!onColumnOrderChange || !over || active.id === over.id) return;
    const oldIndex = orderedColumns.findIndex((c) => c.key === active.id);
    const newIndex = orderedColumns.findIndex((c) => c.key === over.id);
    onColumnOrderChange(
      arrayMove(orderedColumns, oldIndex, newIndex).map((c) => c.key),
    );
  }

  const headerCells = orderedColumns.map((col) =>
    onColumnOrderChange ? (
      <SortableTh key={col.key} col={col} />
    ) : (
      <PlainTh key={col.key} col={col} />
    ),
  );

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
            {onColumnOrderChange ? (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
                modifiers={[restrictToHorizontalAxis]}
              >
                <SortableContext
                  items={orderedColumns.map((c) => c.key)}
                  strategy={horizontalListSortingStrategy}
                >
                  <tr>{headerCells}</tr>
                </SortableContext>
              </DndContext>
            ) : (
              <tr>{headerCells}</tr>
            )}
            {onColumnFilterChange && (
              <tr className="border-t border-slate-200">
                {orderedColumns.map((col) => (
                  <th key={col.key} className="px-2 py-1.5">
                    <ColumnFilterControl
                      col={col}
                      filter={columnFilters?.[col.key]}
                      onChange={onColumnFilterChange}
                      labels={filterLabels}
                    />
                  </th>
                ))}
              </tr>
            )}
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading
              ? Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>
                    {orderedColumns.map((col) => (
                      <td key={col.key} className="px-4 py-3">
                        <div className="h-4 bg-slate-100 rounded animate-pulse w-3/4" />
                      </td>
                    ))}
                  </tr>
                ))
              : groupedSections
                ? groupedSections.flatMap(([key, groupRows]) => {
                    const collapsed = collapsedGroups.has(key);
                    return [
                      <tr
                        key={`group-${key}`}
                        onClick={() => toggleGroup(key)}
                        className="bg-primary/10 hover:bg-primary/15 cursor-pointer"
                      >
                        <td colSpan={orderedColumns.length} className="px-4 py-2.5">
                          <span className="inline-flex items-center gap-1.5 font-bold text-primary">
                            <ChevronDown
                              size={13}
                              strokeWidth={2}
                              className={`text-primary transition-transform ${collapsed ? '-rotate-90' : ''}`}
                            />
                            {renderGroupHeader
                              ? renderGroupHeader(key, groupRows)
                              : `${key} (${groupRows.length})`}
                          </span>
                        </td>
                      </tr>,
                      ...(collapsed
                        ? []
                        : groupRows.map((row, i) => (
                            <tr
                              key={row[rowKey] ?? `${key}-${i}`}
                              onClick={() => onRowClick?.(row)}
                              className={`hover:bg-slate-50 transition ${onRowClick ? 'cursor-pointer' : ''}`}
                            >
                              {orderedColumns.map((col) => (
                                <td
                                  key={col.key}
                                  className={`px-4 py-3 ${col.align === 'right' ? 'text-right' : ''}`}
                                >
                                  <FilterableCell col={col} row={row} onFilterOnValue={onFilterOnValue} labels={filterLabels} />
                                </td>
                              ))}
                            </tr>
                          ))),
                    ];
                  })
                : rows.map((row, i) => (
                  <tr
                    key={row[rowKey] ?? i}
                    onClick={() => onRowClick?.(row)}
                    className={`hover:bg-slate-50 transition ${onRowClick ? 'cursor-pointer' : ''}`}
                  >
                    {orderedColumns.map((col) => (
                      <td
                        key={col.key}
                        className={`px-4 py-3 ${col.align === 'right' ? 'text-right' : ''}`}
                      >
                        <FilterableCell col={col} row={row} onFilterOnValue={onFilterOnValue} labels={filterLabels} />
                      </td>
                    ))}
                  </tr>
                ))}
          </tbody>
        </table>
      </div>

      {!loading && rows.length === 0 && emptyState}

      {hasPagination && !loading && rows.length > 0 && (
        <TablePagination
          page={page}
          total={total}
          perPage={perPage}
          onPrev={onPrev}
          onNext={onNext}
        />
      )}
    </div>
  );
}

export function TablePagination({ page, total, perPage = 10, onPrev, onNext }) {
  const pages = Math.ceil(total / perPage);
  return (
    <div className="px-4 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between text-[12px] text-slate-500">
      <span>
        {total} résultat{total !== 1 ? 's' : ''} · page {page}/{pages}
      </span>
      <div className="flex gap-1">
        <button
          onClick={onPrev}
          disabled={page <= 1}
          className="h-7 px-2.5 rounded border border-slate-200 bg-white font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-40"
        >
          <ChevronLeft size={13} />
        </button>
        <button
          onClick={onNext}
          disabled={page >= pages}
          className="h-7 px-2.5 rounded border border-slate-200 bg-white font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-40"
        >
          <ChevronRight size={13} />
        </button>
      </div>
    </div>
  );
}

export default Table;
